// The customer app's two modes (ADR 0035, docs/specs/customer-app.md 2.4 and 2.6): App at
// `/app`, Admin at `/admin`, on one origin. Pure rules plus the small storage the switch uses.
// Storage may be missing (private windows, previews): every read and write is guarded.

export type Mode = "app" | "admin";

/** Which modes a member may open (`Me.modes`). */
export interface Modes {
  admin: boolean;
  app: boolean;
}

/** The roles the landing rule reads (`Me.roles`). */
export interface ModeRoles {
  rep: boolean;
  support?: boolean;
  manager: boolean;
  admin: boolean;
}

export const MODE_HOME: Record<Mode, string> = { app: "/app", admin: "/admin" };

/** `localStorage`: the mode used last on this device. Decides where `/` lands. */
export const LAST_MODE_KEY = "kite.mode.last";
/** `sessionStorage`: the last page of each mode in this tab. */
export const lastPageKey = (mode: Mode) => `kite.mode.page.${mode}`;

/** The modes of a member from their roles, for a server that does not send `modes` yet. */
export function modesOf(me: { roles: ModeRoles; modes?: Modes }): Modes {
  if (me.modes) return me.modes;
  const r = me.roles;
  return { admin: r.admin, app: r.rep || Boolean(r.support) || r.manager || r.admin };
}

/** The mode a path belongs to, or null for shared pages (sign-in, the listen page). */
export function modeOfPath(path: string): Mode | null {
  if (path === "/app" || path.startsWith("/app/")) return "app";
  if (path === "/admin" || path.startsWith("/admin/")) return "admin";
  return null;
}

/**
 * True when the member sees the switch: only with two modes or more. Platform counts only for
 * Kite staff (`Me.platform_url`, absent for everyone else).
 */
export function showsSwitch(modes: Modes, platformUrl?: string | null): boolean {
  return switchModes(modes, platformUrl).length > 1;
}

export interface LandingFacts {
  roles: ModeRoles;
  modes: Modes;
  /** The mode used last on this device, if any. */
  lastMode: Mode | null;
  /** The company's required setup steps are not all done (Admin Home's checklist). */
  setupUnfinished: boolean;
}

/**
 * Where `/` sends a member (spec 2.4). Without Admin: App. With Admin: the mode used last on
 * this device; the first time, Admin while setup is unfinished or when the member has no
 * phone (an admin without a seat), else App.
 */
export function landingMode(f: LandingFacts): Mode {
  if (!f.modes.admin) return "app";
  if (!f.modes.app) return "admin";
  if (f.lastMode) return f.lastMode;
  const phone = f.roles.rep || Boolean(f.roles.support);
  if (!phone) return "admin";
  return f.setupUnfinished ? "admin" : "app";
}

function local(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function session(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readLastMode(): Mode | null {
  try {
    const v = local()?.getItem(LAST_MODE_KEY);
    return v === "app" || v === "admin" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Remembers the mode in use on this device and the page in this tab. `tab` is this tab's
 * `sessionStorage` (tests pass their own, one per tab).
 */
export function rememberPage(mode: Mode, path: string, tab: Storage | null = session()): void {
  try {
    local()?.setItem(LAST_MODE_KEY, mode);
  } catch {
    // Storage off: the switch still works, it just forgets.
  }
  if (modeOfPath(path.split(/[?#]/)[0] ?? path) !== mode) return;
  try {
    tab?.setItem(lastPageKey(mode), path);
  } catch {
    // As above.
  }
}

/**
 * Where a segment of the switch points: the page this tab last showed in that mode, else the
 * mode's home. A new tab has no history, so it opens the home.
 */
export function switchHref(mode: Mode, tab: Storage | null = session()): string {
  try {
    const p = tab?.getItem(lastPageKey(mode));
    if (p && modeOfPath(p.split(/[?#]/)[0] ?? p) === mode) return p;
  } catch {
    // Storage off.
  }
  return MODE_HOME[mode];
}

/**
 * The address for a page in a mode the member cannot open (spec 2.4): their own mode's home,
 * with the toast the shell shows. Null when the page is theirs.
 */
export function refusedRedirect(path: string, modes: Modes): { to: string; toast: string } | null {
  const m = modeOfPath(path);
  if (m === "admin" && !modes.admin)
    return { to: MODE_HOME.app, toast: "That page is for admins." };
  return null;
}

// ------------------------------------------------------------------------------------------
// App | Admin | Platform (DESIGN.md "Mode switch", approved 2026-11-01)
// ------------------------------------------------------------------------------------------

/** A segment of the switch: the customer app's two modes, and the ops console for Kite staff. */
export type SwitchMode = Mode | "platform";

export const SWITCH_LABEL: Record<SwitchMode, string> = {
  app: "App",
  admin: "Admin",
  platform: "Platform",
};

/** Where the ops console opens with nothing to resume. */
export const PLATFORM_HOME = "/companies";

/** `sessionStorage` on the ops host: the last ops page in this tab. */
export const PLATFORM_PAGE_KEY = "kite.mode.page.platform";

/**
 * The segments a person sees, in order: App and Admin from `Me.modes`, Platform only with
 * `Me.platform_url`. One segment or none means no switch.
 */
export function switchModes(modes: Modes, platformUrl?: string | null): SwitchMode[] {
  const out: SwitchMode[] = [];
  if (modes.app) out.push("app");
  if (modes.admin) out.push("admin");
  if (platformUrl) out.push("platform");
  return out;
}

function split(href: string): { path: string; search: URLSearchParams } {
  const q = href.indexOf("?");
  const raw = q < 0 ? href : href.slice(0, q);
  const rest = q < 0 ? "" : href.slice(q + 1);
  const path = (raw.split("#")[0] ?? "").replace(/\/+$/, "") || "/";
  return { path, search: new URLSearchParams(rest.split("#")[0] ?? "") };
}

const ID = /^[A-Za-z0-9_-]{1,64}$/;
const ok = (id: string | null | undefined): id is string => Boolean(id && ID.test(id));
const SEG = "([^/?#]+)";

/**
 * The page in `to` about the same thing as `here` (a campaign, a call, a teammate, a
 * conversation, a problem report), or null when `here` is a section page with no match.
 * `here` is a path with its search, App paths under `/app`. `tenantId` is the signed-in
 * member's company, needed for a report on the ops console.
 */
export function matchingPage(here: string, to: SwitchMode, tenantId?: string): string | null {
  const { path, search } = split(here);
  const m = (re: string) => path.match(new RegExp(`^${re}$`));
  const from = modeOfPath(path);
  if (from === "app" && to === "admin") {
    if (m("/app/campaigns")) {
      const id = search.get("id");
      return ok(id) ? `/admin/campaigns/${id}` : null;
    }
    const call =
      m(`/app/phone/calls/${SEG}`) ?? m(`/app/history/${SEG}`) ?? m(`/app/team/[^/]+/calls/${SEG}`);
    if (call && ok(call[1])) return `/admin/calls/${call[1]}`;
    const mate = m(`/app/team/${SEG}(?:/[^/]+)?`);
    if (mate && ok(mate[1])) return `/admin/people?member=${mate[1]}`;
    if (m("/app/messages")) {
      const c = search.get("c");
      return ok(c) ? `/admin/messages/${c}` : null;
    }
    return null;
  }
  if (from === "admin" && to === "app") {
    const camp = m(`/admin/campaigns/${SEG}`);
    if (camp && ok(camp[1])) return `/app/campaigns?id=${camp[1]}`;
    const call = m(`/admin/calls/${SEG}`);
    if (call && ok(call[1])) return `/app/phone/calls/${call[1]}`;
    if (m("/admin/people")) {
      const u = search.get("member");
      return ok(u) ? `/app/team/${u}` : null;
    }
    const conv = m(`/admin/messages/${SEG}`);
    if (conv && ok(conv[1]) && !["opt-outs", "templates", "texting"].includes(conv[1]))
      return `/app/messages?c=${conv[1]}`;
    return null;
  }
  if (from === "admin" && to === "platform") {
    const rep = m(`/admin/support/${SEG}`);
    if (rep && ok(rep[1]) && ok(tenantId)) return `/reports/${tenantId}/${rep[1]}`;
  }
  // Platform shows other companies' things, which never match the person's own company.
  return null;
}

export interface SwitchContext {
  /** The mode on screen. */
  current: SwitchMode;
  /** This page: path and search (App paths under `/app`). */
  here: string;
  /** `Me.platform_url`: the ops console's resume address. */
  platformUrl?: string | null;
  /** On the ops console: the customer app's origin (`/v1/platform/me` `app_url`). */
  appUrl?: string | null;
  /** The signed-in member's company, for a report on the ops console. */
  tenantId?: string;
  /** This page's origin, for "Back to Admin" on the ops sign-in. */
  origin?: string;
  /** This tab's `sessionStorage`. */
  tab?: Storage | null;
}

/**
 * Where a segment goes (DESIGN.md "Where a switch lands"): the matching page, else the last
 * page this tab showed in that mode, else its home. Between App and Admin (one origin) the tab's
 * memory is read here; the ops console is another host, so a hop there or back goes through
 * that host's `resume`, which reads its own memory.
 */
export function switchTarget(to: SwitchMode, c: SwitchContext): string {
  const tab = c.tab === undefined ? session() : c.tab;
  if (to === c.current) return to === "platform" ? PLATFORM_HOME : MODE_HOME[to];
  if (to === "platform") {
    const base = c.platformUrl ?? "";
    const q = new URLSearchParams();
    const match = matchingPage(c.here, to, c.tenantId);
    if (match) q.set("to", match);
    q.set("from", c.current);
    if (c.origin) q.set("back", c.origin);
    return `${base}${base.includes("?") ? "&" : "?"}${q.toString()}`;
  }
  if (c.current === "platform") {
    const origin = (c.appUrl ?? "").replace(/\/+$/, "");
    return `${origin}${MODE_HOME[to]}/resume`;
  }
  return matchingPage(c.here, to, c.tenantId) ?? switchHref(to, tab);
}

const LOCAL_PATH = /^\/(?!\/)/;

/**
 * A mode's `resume` page (the hop from another host): `to` when it is a page of this mode,
 * else the last page this tab showed in the mode, else the home.
 */
export function resumeTarget(
  mode: SwitchMode,
  to?: string | null,
  tab: Storage | null = session(),
): string {
  if (mode === "platform") {
    const fine = (p: string | null | undefined): p is string =>
      Boolean(p && LOCAL_PATH.test(p) && !/^\/(resume|signin)\b/.test(p));
    if (fine(to)) return to;
    try {
      const p = tab?.getItem(PLATFORM_PAGE_KEY);
      if (fine(p)) return p;
    } catch {
      // Storage off.
    }
    return PLATFORM_HOME;
  }
  if (to && modeOfPath(split(to).path) === mode && !split(to).path.endsWith("/resume")) return to;
  const last = switchHref(mode, tab);
  return split(last).path.endsWith("/resume") ? MODE_HOME[mode] : last;
}

/** The ops console's own page memory, per tab, on its own host. */
export function rememberPlatformPage(path: string, tab: Storage | null = session()): void {
  if (!LOCAL_PATH.test(path) || /^\/(resume|signin)\b/.test(path)) return;
  try {
    tab?.setItem(PLATFORM_PAGE_KEY, path);
  } catch {
    // Storage off.
  }
}

/**
 * "Back to Admin" on the ops sign-in: the customer app's origin a switch came from, only when
 * it is https (or http on localhost) on a sibling host of `here` (app.kitevoice.com beside
 * ops.kitevoice.com), so the link never leaves Kite.
 */
export function siblingOrigin(back: string | null | undefined, here: string): string | null {
  if (!back) return null;
  try {
    const b = new URL(back);
    const h = new URL(here);
    if (b.origin !== back.replace(/\/+$/, "")) return null;
    const isLocal = (host: string) => host === "localhost" || host.endsWith(".localhost");
    if (b.protocol !== "https:" && !(isLocal(b.hostname) && b.protocol === "http:")) return null;
    const parent = (host: string) => host.split(".").slice(1).join(".");
    const sameParent =
      parent(b.hostname).includes(".") && parent(b.hostname) === parent(h.hostname);
    const bothLocal = isLocal(b.hostname) && isLocal(h.hostname);
    return sameParent || bothLocal ? b.origin : null;
  } catch {
    return null;
  }
}
